import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from '@stencil/core'

import { CssClassMap } from '../../utils/interfaces'
import Helpers from '../../utils/helpers'

/**
 * Posições possíveis para a label.
 */
export type LoadingLabelPosition = 'top' | 'right' | 'bottom' | 'left'

/**
 * Modo de exibição do loading.
 */
export type LoadingMode = 'spinner' | 'progress'

/**
 * Tamanho visual do loading.
 */
export type LoadingSize = 'small' | 'medium' | 'large'

/**
 * Velocidade da animação/transição.
 */
export type LoadingSpeed = 'slow' | 'normal' | 'fast'

/**
 * Comportamento ao atingir 100% no modo `determinate`.
 */
export type LoadingCompletionBehavior = 'persist' | 'reset' | 'hide'

/**
 * Payload do evento de mudança de progresso.
 */
export type LoadingChangeDetail = {
  value: number
}

/**
 * Payload do evento de conclusão do progresso.
 */
export type LoadingCompleteDetail = {
  value: number
  completion: LoadingCompletionBehavior
}

/**
 * Payload do evento de reinício e cancelamento do progresso.
 */
export type LoadingValueDetail = {
  value: number
}

/**
 * Payload do evento de estado lógico no modo indeterminado.
 */
export type LoadingIndeterminateStateDetail = {
  active: boolean
}

/** Mapa que relaciona `LoadingMode` e `LoadingSize` com a dimensão do SVG em pixels. */
const DIMENSION_MAP: Record<LoadingMode, Record<LoadingSize, number>> = {
  spinner: {
    small: 24,
    medium: 44,
    large: 64,
  },
  progress: {
    small: 64,
    medium: 84,
    large: 104,
  },
}

/** Mapa que relaciona `LoadingMode` e `LoadingSize` com a espessura do traço. */
const STROKE_WIDTH_MAP: Record<LoadingMode, Record<LoadingSize, number>> = {
  spinner: {
    small: 2,
    medium: 3,
    large: 4,
  },
  progress: {
    small: 3,
    medium: 4,
    large: 5,
  },
}

/** Mapa que relaciona `LoadingSize` com o tamanho da fonte do percentual no modo `progress`. */
const PERCENTAGE_FONT_SIZE_MAP: Record<LoadingSize, string> = {
  small: 'var(--font-size-scale-base)',
  medium: 'var(--font-size-scale-up-02)',
  large: 'var(--font-size-scale-up-03)',
}

/**
 * Mapa que relaciona `LoadingSpeed` com a duração da animação (s)
 */
const SPEED_MAP: Record<LoadingSpeed, number> = {
  fast: 1.4,
  normal: 2,
  slow: 2.6,
}

/**
 * Mapa que relaciona `LoadingSpeed` com a duração da transição (s) no modo `progress`.
 */
const TRANSITION_MAP: Record<LoadingSpeed, number> = {
  fast: 0.3,
  normal: 0.5,
  slow: 0.8,
}

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/loading?tab=designer).
 */
@Component({
  tag: 'br-loading',
  styleUrl: 'loading.scss',
  shadow: true,
})
export class Loading {
  /**
   * Define a posição da label em relação ao loading.
   * @default 'bottom'
   */
  @Prop({ reflect: true }) labelPosition: LoadingLabelPosition = 'bottom'
  /**
   * Referência ao elemento host do componente, permitindo manipulação direta do DOM para controle de visibilidade e outras interações.
   */
  @Element() el!: HTMLBrLoadingElement

  /**
   * Identificador único do componente, útil para acessibilidade e testes.
   * Se não for fornecido, um ID único será gerado automaticamente.
   * @default Gerado automaticamente no formato 'loading-xxxx' onde 'xxxx' é uma sequência aleatória.
   */
  @Prop({ reflect: true }) customId?: string = Helpers.generateUniqueId('loading-')

  /**
   * Define o modo de exibição do componente.
   * @default 'spinner'
   */
  @Prop({ reflect: true }) mode: LoadingMode = 'spinner'

  /**
   * Define o tamanho visual nos modos `spinner` e `progress`.
   * @default 'medium'
   */
  @Prop({ reflect: true }) size: LoadingSize = 'medium'

  /**
   * Define a velocidade da animação/transição.
   * @default 'normal'
   */
  @Prop({ reflect: true }) speed: LoadingSpeed = 'normal'

  /**
   * Define o rótulo exibido no modo `spinner`.
   * No modo `progress`, esta prop não tem efeito.
   * @default undefined
   */
  @Prop({ reflect: true }) label?: string

  /**
   * Define o progresso no modo `progress`.
   * No modo `spinner`, esta prop não tem efeito.
   * Valores inválidos são normalizados para `0`.
   * @default 0
   */
  @Prop({ reflect: true, mutable: true }) value: number = 0

  /**
   * Define o comportamento quando o progresso atinge 100.
   * No modo `spinner`, esta prop não tem efeito.
   * @default 'persist'
   */
  @Prop({ reflect: true }) completion: LoadingCompletionBehavior = 'persist'

  /**
   * Define se o botão de cancelamento será exibido.
   * Ao pressionar o botão, o evento `brLoadingCancel` é emitido.
   * No modo `spinner`, esta prop não tem efeito.
   * @default false
   */
  @Prop({ reflect: true }) cancelable: boolean = false

  /**
   * Define o texto do botão de cancelamento.
   * No modo `spinner`, esta prop não tem efeito.
   * @default 'Cancelar'
   */
  @Prop({ reflect: true }) cancelLabel: string = 'Cancelar'

  /**
   * Valor exibido no texto de progresso durante a transição.
   */
  @State() private displayedValue: number = 0

  /**
   * Referência da animação ativa do texto de progresso.
   */
  private progressAnimationFrameId?: number

  /** Último estado lógico de atividade no modo `spinner`. */
  private isIndeterminateActive: boolean = false

  /**
   * Notifica mudança de progresso no modo `progress`.
   * @event brLoadingChange
   * Payload (`LoadingChangeDetail`):
   * - `value` (`number`): progresso normalizado (0 a 100).
   */
  @Event({
    eventName: 'brLoadingChange',
    composed: true,
    cancelable: false,
    bubbles: true,
  })
  brLoadingChange!: EventEmitter<LoadingChangeDetail>

  /**
   * Notifica conclusão do progresso no modo `progress`.
   * @event brLoadingComplete
   * Payload (`LoadingCompleteDetail`):
   * - `value` (`number`): progresso normalizado (0 a 100).
   * - `completion` (`LoadingCompletionBehavior`): ação aplicada ao atingir 100%.
   *   - `persist`: mantém o loading em 100%.
   *   - `reset`: reinicia o progresso para 0.
   *   - `hide`: oculta o componente.
   */
  @Event({
    eventName: 'brLoadingComplete',
    composed: true,
    cancelable: false,
    bubbles: true,
  })
  brLoadingComplete!: EventEmitter<LoadingCompleteDetail>

  /**
   * Notifica reinício do progresso no modo `progress`.
   * @event brLoadingReset
   * Payload (`LoadingValueDetail`):
   * - `value` (`number`): valor do progresso após o reset.
   */
  @Event({
    eventName: 'brLoadingReset',
    composed: true,
    cancelable: false,
    bubbles: true,
  })
  brLoadingReset!: EventEmitter<LoadingValueDetail>

  /**
   * Notifica clique no botão de cancelamento no modo `progress`.
   * Este evento não altera o progresso automaticamente.
   * A aplicação consumidora define a ação após o cancelamento (ex.: resetar ou ocultar).
   * @event brLoadingCancel
   * Payload (`LoadingValueDetail`):
   * - `value` (`number`): valor de progresso no momento do cancelamento.
   */
  @Event({
    eventName: 'brLoadingCancel',
    composed: true,
    cancelable: false,
    bubbles: true,
  })
  brLoadingCancel!: EventEmitter<LoadingValueDetail>

  /**
   * Notifica mudança do estado lógico no modo `spinner`.
   * @event brIndeterminateStateChange
   * Payload (`LoadingIndeterminateStateDetail`):
   * - `active` (`boolean`): `true` quando ativo; `false` quando inativo.
   */
  @Event({
    eventName: 'brIndeterminateStateChange',
    composed: true,
    cancelable: false,
    bubbles: true,
  })
  brIndeterminateStateChange!: EventEmitter<LoadingIndeterminateStateDetail>

  /**
   * Notifica que o componente foi exibido.
   * @event brDidShow
   * Payload: `void`.
   */
  @Event({
    eventName: 'brDidShow',
    composed: true,
    cancelable: false,
    bubbles: true,
  })
  brDidShow!: EventEmitter<void>

  /**
   * Notifica que o componente foi ocultado.
   * @event brDidHide
   * Payload: `void`.
   */
  @Event({
    eventName: 'brDidHide',
    composed: true,
    cancelable: false,
    bubbles: true,
  })
  brDidHide!: EventEmitter<void>

  /**
   * Reage a mudanças em `value` no modo `progress`.
   * @param newValue Novo valor recebido.
   */
  @Watch('value')
  handleCompletion(newValue: number) {
    const sanitizedValue = this.normalizeInputValue(newValue)
    if (sanitizedValue !== newValue) {
      return
    }

    if (this.mode !== 'progress') return

    this.brLoadingChange.emit({ value: this.normalizedValue })

    this.syncDisplayedProgress(this.normalizedValue)

    this.applyCompletionBehavior(newValue)
  }

  /**
   * Reage a mudanças em `mode` e atualiza o estado lógico de atividade.
   */
  @Watch('mode')
  handleModeChange() {
    this.syncIndeterminateActiveState()
  }

  /**
   * Etapa do ciclo de vida: antes da primeira renderização.
   * Inicializa o estado visual do progresso.
   */
  componentWillLoad() {
    this.normalizeInputValue(this.value)
    this.initializeProgressDisplay()
    this.syncIndeterminateActiveState()
  }

  /**
   * Etapa do ciclo de vida: desmontagem do componente.
   * Libera recursos de animação pendentes.
   */
  disconnectedCallback() {
    this.cleanupProgressResources()

    if (this.isIndeterminateActive) {
      this.isIndeterminateActive = false
      this.brIndeterminateStateChange.emit({ active: false })
    }
  }

  /**
   * Define o valor do progresso.
   * @param value Valor desejado.
   * @returns Progresso normalizado.
   */
  @Method()
  async setValue(value: number): Promise<{ value: number }> {
    this.normalizeInputValue(value)
    return { value: this.normalizedValue }
  }

  /**
   * Soma um valor ao progresso atual.
   * @param step Incremento aplicado.
   * @returns Progresso normalizado.
   */
  @Method()
  async incrementValue(step: number = 1): Promise<{ value: number }> {
    this.value = this.normalizedValue + step
    return { value: this.normalizedValue }
  }

  /**
   * Define o progresso como concluído.
   * @returns Progresso normalizado.
   */
  @Method()
  async complete(): Promise<{ value: number }> {
    this.value = 100
    return { value: this.normalizedValue }
  }

  /**
   * Reinicia o progresso e exibe o componente.
   * @returns Progresso normalizado.
   */
  @Method()
  async reset(): Promise<{ value: number }> {
    this.value = 0
    this.setVisibility(true)

    if (this.mode === 'progress') {
      this.brLoadingReset.emit({ value: this.normalizedValue })
    }

    return { value: this.normalizedValue }
  }

  /**
   * Exibe o componente.
   * @returns Estado de visibilidade.
   */
  @Method()
  async show(): Promise<{ visible: boolean }> {
    this.setVisibility(true)
    return { visible: true }
  }

  /**
   * Oculta o componente.
   * @returns Estado de visibilidade.
   */
  @Method()
  async hide(): Promise<{ visible: boolean }> {
    this.setVisibility(false)
    return { visible: false }
  }

  /**
   * Trata o clique no botão de cancelamento.
   */
  private handleCancelClick = () => {
    if (this.mode !== 'progress' || !this.cancelable) return

    this.brLoadingCancel.emit({ value: this.normalizedValue })
  }

  /**
   * Aplica a ação configurada ao atingir 100% no modo `progress`.
   * @param value Valor atual do progresso.
   */
  private applyCompletionBehavior(value: number) {
    if (this.mode !== 'progress') return

    if (value >= 100) {
      this.brLoadingComplete.emit({ value: 100, completion: this.completion })

      if (this.completion === 'reset') {
        setTimeout(() => this.reset(), 300)
      }

      if (this.completion === 'hide') {
        this.setVisibility(false)
      }
    }
  }

  /**
   * Inicializa o valor exibido no texto de progresso.
   */
  private initializeProgressDisplay() {
    this.displayedValue = this.normalizedValue
  }

  /**
   * Libera recursos usados pela animação de progresso.
   */
  private cleanupProgressResources() {
    this.stopProgressAnimation()
  }

  /**
   * Emite mudança de estado lógico para o modo `spinner` quando necessário.
   */
  private syncIndeterminateActiveState() {
    const shouldBeActive = this.mode === 'spinner' && this.el.style.display !== 'none'

    if (this.isIndeterminateActive === shouldBeActive) return

    this.isIndeterminateActive = shouldBeActive
    this.brIndeterminateStateChange.emit({ active: shouldBeActive })
  }

  /**
   * Sincroniza o texto de percentual com a transição visual do círculo.
   * @param targetValue Valor final do progresso normalizado.
   */
  private syncDisplayedProgress(targetValue: number) {
    if (this.mode !== 'progress') {
      this.displayedValue = targetValue
      return
    }

    this.stopProgressAnimation()

    const from = this.displayedValue
    const to = targetValue
    const durationMs = TRANSITION_MAP[this.speed] * 1000

    if (from === to || durationMs <= 0) {
      this.displayedValue = to
      return
    }

    const startedAt = performance.now()

    const step = (now: number) => {
      const elapsed = Math.min((now - startedAt) / durationMs, 1)
      const eased = this.easeProgress(elapsed)

      this.displayedValue = Math.round(from + (to - from) * eased)

      if (elapsed < 1) {
        this.progressAnimationFrameId = requestAnimationFrame(step)
        return
      }

      this.displayedValue = to
      this.progressAnimationFrameId = undefined
    }

    this.progressAnimationFrameId = requestAnimationFrame(step)
  }

  /**
   * Interpolação para suavizar a animação do percentual.
   * @param time Progresso normalizado da animação (0 a 1).
   * @returns Valor interpolado entre 0 e 1.
   */
  private easeProgress(time: number): number {
    return time < 0.5 ? 2 * time * time : -1 + (4 - 2 * time) * time
  }

  /** Cancela a animação de percentual em andamento. */
  private stopProgressAnimation() {
    if (this.progressAnimationFrameId === undefined) return

    cancelAnimationFrame(this.progressAnimationFrameId)
    this.progressAnimationFrameId = undefined
  }

  /**
   * Atualiza a visibilidade e emite o evento correspondente.
   * @param visible Define se o componente ficará visível.
   * @private
   */
  private setVisibility(visible: boolean) {
    this.el.style.display = visible ? '' : 'none'
    this.syncIndeterminateActiveState()

    if (visible) {
      this.brDidShow.emit()
      return
    }

    this.brDidHide.emit()
  }

  /**
   * Normaliza um valor arbitrário de progresso para o intervalo de 0 a 100.
   * Valores inválidos (incluindo string vazia e NaN) são convertidos para 0.
   * Também sincroniza `this.value` internamente quando necessário.
   */
  private normalizeInputValue(value: unknown): number {
    const normalizedValue = this.toValidProgressValue(value)

    if (this.value !== normalizedValue) {
      this.value = normalizedValue
    }

    return normalizedValue
  }

  private toValidProgressValue(value: unknown): number {
    if (typeof value === 'string' && value.trim() === '') {
      return 0
    }

    const numericValue = typeof value === 'number' ? value : Number(value)

    if (!Number.isFinite(numericValue)) {
      return 0
    }

    return Math.min(Math.max(numericValue, 0), 100)
  }

  /**
   * Retorna o progresso no intervalo de 0 a 100.
   * @private
   */
  private get normalizedValue(): number {
    return this.toValidProgressValue(this.value)
  }

  /**
   * Retorna a dimensão do SVG.
   * @private
   */
  private get dimension(): number {
    return DIMENSION_MAP[this.mode][this.size]
  }

  /**
   * Retorna a espessura do traço do círculo.
   * @private
   */
  private get strokeWidth(): number {
    return STROKE_WIDTH_MAP[this.mode][this.size]
  }

  /**
   * Retorna o tamanho da fonte do percentual no modo `progress`.
   * @private
   */
  private get percentageFontSize(): string {
    return PERCENTAGE_FONT_SIZE_MAP[this.size]
  }

  /**
   * Retorna a coordenada central do SVG.
   * @private
   */
  private get center(): number {
    return this.dimension / 2
  }

  /**
   * Retorna o raio útil do círculo.
   * @private
   */
  private get radius(): number {
    return this.center - this.strokeWidth / 2
  }

  /**
   * Retorna a circunferência do círculo.
   * @private
   */
  private get circumference(): number {
    return 2 * Math.PI * this.radius
  }

  /**
   * Retorna o deslocamento do traço conforme o progresso.
   * @private
   */
  private get progressOffset(): number {
    const progress = this.normalizedValue / 100
    return this.circumference * (1 - progress)
  }

  /**
   * Monta o mapa de classes CSS do host interno.
   * @returns Mapa de classes aplicadas.
   * @private
   */
  private getCssClassMap(): CssClassMap {
    const cssClassMap: CssClassMap = {
      'br-loading': true,
      [`loading-mode-${this.mode}`]: true,
      [`loading-${this.speed}`]: true,
      [`loading-${this.size}`]: true,
    }

    return cssClassMap
  }

  /**
   * Renderiza o modo `spinner`.
   * @returns Estrutura JSX do spinner.
   */
  private renderIndeterminate() {
    const duration = `${SPEED_MAP[this.speed]}s`

    return this.renderSpinnerWithLabelPosition(duration)
  }

  /**
   * Renderiza spinner com label em qualquer posição.
   */
  private renderSpinnerWithLabelPosition(duration: string) {
    const label = this.label && <div class={`loading-label loading-label-${this.labelPosition}`}>{this.label}</div>
    const spinner = (
      <svg
        class="loading-spinner"
        width={this.dimension}
        height={this.dimension}
        viewBox={`0 0 ${this.dimension} ${this.dimension}`}
        role="progressbar"
        aria-busy="true"
        style={{
          '--loading-animation-duration': duration,
        }}
      >
        <circle
          class="loading-indicator"
          cx={this.center}
          cy={this.center}
          r={this.radius}
          fill="none"
          stroke-width={this.strokeWidth}
          stroke-dasharray={this.circumference}
          stroke-dashoffset={this.circumference * 0.75}
        />
      </svg>
    )

    // Layout flexível para cada posição
    switch (this.labelPosition) {
      case 'top':
        return (
          <div class="loading-wrapper loading-label-top">
            {label}
            {spinner}
          </div>
        )
      case 'right':
        return (
          <div class="loading-wrapper loading-label-right">
            {spinner}
            {label}
          </div>
        )
      case 'left':
        return (
          <div class="loading-wrapper loading-label-left">
            {label}
            {spinner}
          </div>
        )
      case 'bottom':
      default:
        return (
          <div class="loading-wrapper loading-label-bottom">
            {spinner}
            {label}
          </div>
        )
    }
  }

  /**
   * Renderiza o modo `progress`.
   * @returns Estrutura JSX do progresso circular.
   * @private
   */
  private renderDeterminate() {
    const transitionDuration = `${TRANSITION_MAP[this.speed]}s`
    const progressStyle = {
      '--loading-transition-duration': transitionDuration,
      '--loading-percentage-font-size': this.percentageFontSize,
    }

    return (
      <div class="loading-wrapper">
        <svg
          class="loading-progress"
          width={this.dimension}
          height={this.dimension}
          viewBox={`0 0 ${this.dimension} ${this.dimension}`}
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={this.normalizedValue}
          style={progressStyle}
        >
          <circle
            class="loading-spinner-track"
            cx={this.center}
            cy={this.center}
            r={this.radius}
            fill="none"
            stroke-width={this.strokeWidth}
          />

          <circle
            class="loading-indicator-progress"
            cx={this.center}
            cy={this.center}
            r={this.radius}
            fill="none"
            stroke-width={this.strokeWidth}
            stroke-dasharray={this.circumference}
            stroke-dashoffset={this.progressOffset}
          />

          <text class="loading-percentage" x="50%" y="50%" dy=".35em" text-anchor="middle">
            {this.displayedValue}%
          </text>
        </svg>

        {this.cancelable && (
          <br-button emphasis="tertiary" type="button" onClick={this.handleCancelClick}>
            {this.cancelLabel}
          </br-button>
        )}
      </div>
    )
  }

  /**
   * Renderiza o componente conforme o modo atual.
   * @returns Estrutura JSX final.
   */
  render() {
    return (
      <Host>
        <div class={this.getCssClassMap()}>
          {this.mode === 'spinner' ? this.renderIndeterminate() : this.renderDeterminate()}
        </div>
      </Host>
    )
  }
}
